import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../AxiosInstance';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  Box,
  Link,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: '1.8rem', // Increase the size of the navigation arrows
  },
}));

// Helper functions for supplier handling in reorder candidates.
// These are pure and do not depend on React state.

const getSuppliersForUnit = (item, unitId) => {
  if (!unitId) return [];
  const map = item.unitSuppliersByUnitId || {};
  const suppliers = map[unitId];
  return Array.isArray(suppliers) ? suppliers : [];
};

const getDefaultSupplierIdForUnit = (item, unitId) => {
  const suppliers = getSuppliersForUnit(item, unitId);
  if (!suppliers.length) {
    if (item.supplierId != null) {
      return String(item.supplierId);
    }
    return '';
  }

  const primarySupplier = suppliers.find((s) => s && s.isPrimary);
  if (primarySupplier && primarySupplier.supplierId != null) {
    return String(primarySupplier.supplierId);
  }

  const existingId = item.supplierId != null ? String(item.supplierId) : null;
  if (
    existingId &&
    suppliers.some((s) => s && String(s.supplierId) === existingId)
  ) {
    return existingId;
  }

  const first = suppliers[0];
  return first && first.supplierId != null ? String(first.supplierId) : '';
};

const formatSupplierLabel = (supplier) => {
  if (!supplier) return 'Supplier';
  const name = supplier.supplierName || 'Supplier';
  const market = supplier.supplierMarketName || '';
  return market ? `${name} (${market})` : name;
};

// Reorder Inventory page: shows products below target stock, one row per (product, supplier)
// and generates WhatsApp-ready reorder texts.
export const ReorderInventory = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState({});
  const [selectedUnits, setSelectedUnits] = useState({});
  const [reorderQuantities, setReorderQuantities] = useState({});
  const [selectedSuppliers, setSelectedSuppliers] = useState({});
  const [generatedText, setGeneratedText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('asc');
  const [messages, setMessages] = useState([]); // { key, message }
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showFilters, setShowFilters] = useState(false);
  const [productNameFilter, setProductNameFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  useEffect(() => {
    const fetchReorderCandidates = async () => {
      try {
        const response = await axiosInstance.get('/inventories/reorder-candidates');
        const items =
          response.data && Array.isArray(response.data.reorderCandidates)
            ? response.data.reorderCandidates
            : [];

        const initSelected = {};
        const initUnits = {};
        const initReorderQuantities = {};
        const initSuppliers = {};
        items.forEach((item) => {
          const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
          // Auto-select only true reorder candidates (current_stock < stock_limit);
          // other products remain unchecked by default.
          initSelected[key] = !!item.isReorderCandidate;

          const defaultUnitId =
            item.defaultUnitId ||
            (item.unitTypes && item.unitTypes[0] && item.unitTypes[0].unitId) ||
            '';
          initUnits[key] = defaultUnitId;

          // Determine a numeric base quantity, but only prefill the field
          // when it is a positive value. Zero will be shown as blank.
          let baseQty = 0;
          if (Array.isArray(item.unitTypes) && defaultUnitId) {
            const defaultUnit = item.unitTypes.find(
              (u) => u.unitId === defaultUnitId
            );
            if (defaultUnit && typeof defaultUnit.reorderQuantity === 'number') {
              baseQty = defaultUnit.reorderQuantity;
            }
          }
          if (baseQty === 0 && typeof item.reorderQuantity === 'number') {
            baseQty = item.reorderQuantity;
          }

          const displayQty =
            typeof baseQty === 'number' && baseQty > 0 ? String(baseQty) : '';
          initReorderQuantities[key] = displayQty;

          const unitIdForRow = defaultUnitId || null;
          const defaultSupplierId = getDefaultSupplierIdForUnit(item, unitIdForRow);
          initSuppliers[key] = defaultSupplierId;
        });

        setCandidates(items);
        setSelectedMap(initSelected);
        setSelectedUnits(initUnits);
        setReorderQuantities(initReorderQuantities);
        setSelectedSuppliers(initSuppliers);
      } catch (error) {
        console.error('Error fetching reorder candidates:', error);
        setSnackbarSeverity('error');
        setErrorMessage('Failed to fetch reorder candidates.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchReorderCandidates();
  }, []);

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    const visible = getSortedCandidates();

    setSelectedMap((prev) => {
      const next = { ...prev };
      visible.forEach((item) => {
        const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
        next[key] = checked;
      });
      return next;
    });
  };

  const handleToggleRow = (key) => {
    setSelectedMap((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleUnitChange = (item, key, unitId) => {
    setSelectedUnits((prev) => ({
      ...prev,
      [key]: unitId,
    }));
    let baseQty = 0;
    if (Array.isArray(item.unitTypes)) {
      const selectedUnit = item.unitTypes.find((u) => u.unitId === unitId);
      if (selectedUnit && typeof selectedUnit.reorderQuantity === 'number') {
        baseQty = selectedUnit.reorderQuantity;
      }
    } else if (typeof item.reorderQuantity === 'number') {
      // Fallback for older payloads without per-unit data
      baseQty = item.reorderQuantity;
    }

    const displayQty =
      typeof baseQty === 'number' && baseQty > 0 ? String(baseQty) : '';

    setReorderQuantities((prev) => ({
      ...prev,
      [key]: displayQty,
    }));

    const defaultSupplierId = getDefaultSupplierIdForUnit(item, unitId);
    setSelectedSuppliers((prev) => ({
      ...prev,
      [key]: defaultSupplierId,
    }));
  };

  const getEffectiveSupplierForRow = (item, key, unitIdOverride) => {
    const unitId = unitIdOverride || selectedUnits[key] || item.defaultUnitId || '';
    const suppliers = getSuppliersForUnit(item, unitId);

    let supplier = null;

    if (suppliers.length > 0) {
      const selectedId =
        selectedSuppliers[key] || getDefaultSupplierIdForUnit(item, unitId);
      supplier =
        suppliers.find(
          (s) => String(s.supplierId) === String(selectedId)
        ) || suppliers[0];
    }

    if (supplier) {
      return supplier;
    }

    return {
      supplierId: item.supplierId != null ? item.supplierId : null,
      supplierName: item.supplierName || 'Supplier',
      supplierMarketName: item.supplierMarketName || '',
      supplierPhoneNumber: item.supplierPhoneNumber || '',
    };
  };

  const formatBrandVariety = (brand, variety) => {
    const parts = [];
    if (brand) parts.push(brand);
    if (variety) parts.push(variety);
    if (parts.length === 0) return '';
    return parts.join(' / ');
  };

  const handleReorderQuantityChange = (key, value) => {
    setReorderQuantities((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSupplierChange = (key, supplierId) => {
    setSelectedSuppliers((prev) => ({
      ...prev,
      [key]: supplierId,
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortedCandidates = () => {
    let base = candidates;

    const trimmedNameFilter = productNameFilter.trim().toLowerCase();

    if (showFilters && (trimmedNameFilter || supplierFilter)) {
      base = base.filter((item) => {
        let matchesName = true;
        if (trimmedNameFilter) {
          const nameHaystack = `${item.productName || ''} ${item.brand || ''} ${item.variety || ''}`.toLowerCase();
          matchesName = nameHaystack.includes(trimmedNameFilter);
        }

        let matchesSupplier = true;
        if (supplierFilter) {
          const supplierIdStr = String(supplierFilter);

          let rowHasSupplier = false;

          // Check all buying units for this row to see if any configured supplier matches.
          if (Array.isArray(item.unitTypes) && item.unitTypes.length > 0) {
            for (const u of item.unitTypes) {
              if (!u || !u.unitId) continue;
              const suppliersForUnit = getSuppliersForUnit(item, u.unitId);
              if (
                suppliersForUnit.some(
                  (s) => s && String(s.supplierId) === supplierIdStr
                )
              ) {
                rowHasSupplier = true;
                break;
              }
            }
          }

          // As a fallback, also consider the legacy/top-level supplierId on the row.
          if (!rowHasSupplier && item.supplierId != null) {
            rowHasSupplier = String(item.supplierId) === supplierIdStr;
          }

          matchesSupplier = rowHasSupplier;
        }

        return matchesName && matchesSupplier;
      });
    }

    if (!orderBy) {
      // Preserve backend ordering (reorder candidates first) when no sort is active.
      return base;
    }

    const sorted = [...base].sort((a, b) => {
      const keyA = `${a.inventoryId}-${a.productId}-${a.supplierId}`;
      const keyB = `${b.inventoryId}-${b.productId}-${b.supplierId}`;

      let cmp = 0;
      if (orderBy === 'checked') {
        const aChecked = !!selectedMap[keyA];
        const bChecked = !!selectedMap[keyB];
        if (aChecked === bChecked) {
          cmp = 0;
        } else {
          cmp = aChecked ? -1 : 1;
        }
      } else if (orderBy === 'productName') {
        cmp = a.productName.localeCompare(b.productName);
      } else if (orderBy === 'currentStock') {
        cmp = a.currentStock - b.currentStock;
      } else if (orderBy === 'supplier') {
        const aName = a.supplierName || '';
        const bName = b.supplierName || '';
        cmp = aName.localeCompare(bName);
      } else if (orderBy === 'reorderQuantity') {
        const aRaw = reorderQuantities[keyA];
        const bRaw = reorderQuantities[keyB];
        const aQty =
          aRaw === '' || aRaw == null ? 0 : Number(aRaw) || 0;
        const bQty =
          bRaw === '' || bRaw == null ? 0 : Number(bRaw) || 0;
        cmp = aQty - bQty;
      }

      return order === 'asc' ? cmp : -cmp;
    });

    return sorted;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 10;
    setRowsPerPage(value);
    setPage(0);
  };

  const jumpToMessageRowPage = (key) => {
    if (!key) return;
    const sorted = getSortedCandidates();
    const index = sorted.findIndex((item) => {
      const itemKey = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
      return itemKey === key;
    });
    if (index === -1) return;
    const newPage = Math.floor(index / rowsPerPage);
    setPage(newPage);
  };

  const renderSortLabel = (columnKey, label) => {
    const isActive = orderBy === columnKey;
    const isAsc = order === 'asc';

    const upActive = isActive && isAsc;
    const downActive = isActive && !isAsc;

    const inactiveColor = 'rgba(255,255,255,0.7)';
    const activeColor = '#FFFFFF';

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {label}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
          <Box
            component="span"
            sx={{
              fontSize: '0.7rem',
              color: upActive ? activeColor : inactiveColor,
            }}
          >
            ▲
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: '0.7rem',
              color: downActive ? activeColor : inactiveColor,
              mt: '-2px',
            }}
          >
            ▼
          </Box>
        </Box>
      </Box>
    );
  };

  const handleGenerateText = async () => {
    const getQtyForKey = (key) => {
      const raw = reorderQuantities[key];
      if (raw === '' || raw == null) return 0;
      const num = Number(raw);
      return Number.isNaN(num) ? 0 : num;
    };

    const selectedReorders = [];

    candidates.forEach((item) => {
      const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
      if (!selectedMap[key]) return;

      const unitIdForReorder = selectedUnits[key] || item.defaultUnitId || null;
      const qty = getQtyForKey(key);

       const effectiveSupplier = getEffectiveSupplierForRow(
        item,
        key,
        unitIdForReorder
      );
      const supplierIdRaw =
        effectiveSupplier && effectiveSupplier.supplierId != null
          ? effectiveSupplier.supplierId
          : item.supplierId;
      const supplierIdForReorder =
        supplierIdRaw != null ? Number(supplierIdRaw) : null;

      selectedReorders.push({
        productId: item.productId,
        supplierId: supplierIdForReorder,
        unitId: unitIdForReorder,
        orderQuantity: qty,
      });
    });

    if (selectedReorders.length === 0) {
      setSnackbarSeverity('error');
      setErrorMessage('No items selected to generate reorder text.');
      setSnackbarOpen(true);
      setGeneratedText('');
      return;
    }

    try {
      await axiosInstance.post('/inventories/reorders/bulk-upsert', {
        reorders: selectedReorders,
      });
    } catch (error) {
      console.error('Error saving reorders:', error);
      setSnackbarSeverity('error');
      setErrorMessage('Failed to save reorder records. Please try again.');
      setSnackbarOpen(true);
      return;
    }

    const groupedBySupplier = new Map();

    candidates.forEach((item) => {
      const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
      if (!selectedMap[key]) return;

      const unitId = selectedUnits[key] || item.defaultUnitId || null;
      const effectiveSupplier = getEffectiveSupplierForRow(item, key, unitId);

      const supplierKey =
        effectiveSupplier && effectiveSupplier.supplierId != null
          ? String(effectiveSupplier.supplierId)
          : `no-supplier-${
              (effectiveSupplier && effectiveSupplier.supplierName) ||
              item.supplierName ||
              'Supplier'
            }`;

      const unit =
        (item.unitTypes || []).find((u) => u.unitId === unitId) ||
        (item.unitTypes && item.unitTypes[0]);
      const unitTypeLabel = unit ? unit.unitType : '';

      const brandVariety = formatBrandVariety(item.brand, item.variety);
      const brandVarietyWithSize = brandVariety && item.size 
        ? `${brandVariety} / ${item.size}` 
        : brandVariety || item.size || '';
      const brandVarietySegment = brandVarietyWithSize ? ` (${brandVarietyWithSize})` : '';
      const qty = getQtyForKey(key);

      if (!groupedBySupplier.has(supplierKey)) {
        groupedBySupplier.set(supplierKey, {
          supplierName:
            (effectiveSupplier && effectiveSupplier.supplierName) ||
            item.supplierName ||
            'Supplier',
          supplierPhoneNumber:
            (effectiveSupplier && effectiveSupplier.supplierPhoneNumber) ||
            item.supplierPhoneNumber ||
            '',
          rows: [],
        });
      }

      const group = groupedBySupplier.get(supplierKey);
      group.rows.push({
        key,
        productName: item.productName,
        brandVarietySegment,
        qty,
        unitTypeLabel,
      });
    });

    const newMessages = [];

    groupedBySupplier.forEach((group) => {
      const { supplierName, supplierPhoneNumber, rows } = group;
      if (!rows.length) return;

      const itemsText = rows
        .map(
          (row) =>
            `- ${row.productName}${row.brandVarietySegment}:  ${row.qty} ${row.unitTypeLabel}`
        )
        .join('\n');

      const msg = `Dear ${supplierName || 'Supplier'} :\n\n` +
        `I need to reorder the following:\n\n` +
        `${itemsText}\n\n` +
        `Thank you!`;

      const firstRowKey = rows[0].key;

      newMessages.push({
        key: firstRowKey,
        message: msg,
        supplierPhoneNumber,
      });
    });

    if (newMessages.length === 0) {
      setSnackbarSeverity('error');
      setErrorMessage('No items selected to generate reorder text.');
      setSnackbarOpen(true);
      setGeneratedText('');
      return;
    }

    setMessages(newMessages);
    setCurrentMessageIndex(0);
    setHighlightedKey(newMessages[0].key);
    setGeneratedText(newMessages[0].message);
    jumpToMessageRowPage(newMessages[0].key);
  };

  const handlePreviousMessage = () => {
    if (currentMessageIndex <= 0) return;
    const newIndex = currentMessageIndex - 1;
    const msg = messages[newIndex];
    setCurrentMessageIndex(newIndex);
    setGeneratedText(msg.message);
    setHighlightedKey(msg.key);
    jumpToMessageRowPage(msg.key);
  };

  const handleNextMessage = () => {
    if (currentMessageIndex >= messages.length - 1) return;
    const newIndex = currentMessageIndex + 1;
    const msg = messages[newIndex];
    setCurrentMessageIndex(newIndex);
    setGeneratedText(msg.message);
    setHighlightedKey(msg.key);
     jumpToMessageRowPage(msg.key);
  };

  const handleSendWhatsApp = () => {
    if (!messages.length) {
      setSnackbarSeverity('error');
      setErrorMessage('No message to send. Please generate a reorder message first.');
      setSnackbarOpen(true);
      return;
    }

    const current = messages[currentMessageIndex] || messages[0];
    if (!current) {
      setSnackbarSeverity('error');
      setErrorMessage('No message to send. Please generate a reorder message first.');
      setSnackbarOpen(true);
      return;
    }

    const phoneRaw = current.supplierPhoneNumber;
    if (!phoneRaw) {
      setSnackbarSeverity('error');
      setErrorMessage('No phone number is set for this supplier. Please add a phone number to the supplier first.');
      setSnackbarOpen(true);
      return;
    }

    const phone = String(phoneRaw).replace(/[^0-9]/g, '');
    if (!phone) {
      setSnackbarSeverity('error');
      setErrorMessage('Supplier phone number is invalid.');
      setSnackbarOpen(true);
      return;
    }

    const text = current.message || generatedText || '';
    if (!text) {
      setSnackbarSeverity('error');
      setErrorMessage('No message text to send.');
      setSnackbarOpen(true);
      return;
    }

    const encodedText = encodeURIComponent(text);
    const url = `https://wa.me/${phone}?text=${encodedText}`;

    try {
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening WhatsApp link:', err);
      setSnackbarSeverity('error');
      setErrorMessage('Unable to open WhatsApp.');
      setSnackbarOpen(true);
    }
  };

  const handleCopyToClipboard = async () => {
    const current = messages[currentMessageIndex] || messages[0];
    const text = (current && current.message) || generatedText || '';

    if (!text) {
      setSnackbarSeverity('error');
      setErrorMessage('No message text to copy. Please generate a reorder message first.');
      setSnackbarOpen(true);
      return;
    }

    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setSnackbarSeverity('success');
      setErrorMessage('Copied to clipboard.');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setSnackbarSeverity('error');
      setErrorMessage('Failed to copy to clipboard.');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setSnackbarSeverity('error');
  };

  const supplierOptions = React.useMemo(() => {
    const map = new Map();
    candidates.forEach((item) => {
      // Collect suppliers from unitSuppliersByUnitId (all configured suppliers)
      if (item.unitSuppliersByUnitId) {
        Object.values(item.unitSuppliersByUnitId).forEach((list) => {
          if (!Array.isArray(list)) return;
          list.forEach((s) => {
            if (!s || s.supplierId == null) return;
            if (!map.has(s.supplierId)) {
              map.set(s.supplierId, formatSupplierLabel(s));
            }
          });
        });
      }

      // Fallback: include the row's top-level supplierId if present.
      if (item.supplierId != null && !map.has(item.supplierId)) {
        const fallbackSupplier = {
          supplierId: item.supplierId,
          supplierName: item.supplierName || 'Supplier',
          supplierMarketName: item.supplierMarketName || '',
        };
        map.set(item.supplierId, formatSupplierLabel(fallbackSupplier));
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [candidates]);
  const sortedCandidates = getSortedCandidates();
  const allSelected =
    sortedCandidates.length > 0 &&
    sortedCandidates.every((item) => {
      const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
      return !!selectedMap[key];
    });

  const someSelected = sortedCandidates.some((item) => {
    const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
    return !!selectedMap[key];
  });
  const totalSelectedCount = candidates.reduce((count, item) => {
    const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
    return count + (selectedMap[key] ? 1 : 0);
  }, 0);
  const visibleSelectedCount = sortedCandidates.reduce((count, item) => {
    const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
    return count + (selectedMap[key] ? 1 : 0);
  }, 0);
  const paginatedCandidates = sortedCandidates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const hasHiddenSelectedByFilters =
    totalSelectedCount > 0 && visibleSelectedCount < totalSelectedCount;

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Reorder Inventory
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }} align="center">
        Which items do you want to restock?
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : candidates.length === 0 ? (
        <Typography>No items are currently below their target stock.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showFilters}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowFilters(checked);
                    if (checked) {
                      setPage(0);
                    } else {
                      setProductNameFilter('');
                      setSupplierFilter('');
                      setPage(0);
                    }
                  }}
                />
              }
              label="Enable Filters"
            />
          </Box>

          {showFilters && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Product Name"
                variant="outlined"
                size="small"
                value={productNameFilter}
                onChange={(e) => {
                  setProductNameFilter(e.target.value);
                  setPage(0);
                }}
              />
              <FormControl sx={{ minWidth: 200 }} size="small">
                <Select
                  displayEmpty
                  value={supplierFilter}
                  onChange={(e) => {
                    setSupplierFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">
                    <em>All suppliers</em>
                  </MenuItem>
                  {supplierOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasHiddenSelectedByFilters && (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Some selected items are hidden by filters but will still be included when you generate reorder messages.
              </Typography>
            </Box>
          )}

          <TableContainer component={Paper} sx={{ boxShadow: 3, marginBottom: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    padding="checkbox"
                    onClick={() => handleRequestSort('checked')}
                    sx={{ cursor: 'pointer', whiteSpace: 'nowrap', width: 110 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 0.25,
                      }}
                    >
                      <Checkbox
                        indeterminate={someSelected && !allSelected}
                        checked={allSelected}
                        onChange={handleToggleAll}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: '#FFFFFF',
                          '&.Mui-checked': { color: '#FFFFFF' },
                          '&.MuiCheckbox-indeterminate': { color: '#FFFFFF' },
                        }}
                      />
                      {renderSortLabel('checked', '')}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => handleRequestSort('productName')}
                    sx={{ cursor: 'pointer' }}
                  >
                    {renderSortLabel('productName', 'Product Name')}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => handleRequestSort('currentStock')}
                    sx={{ cursor: 'pointer' }}
                    align="center"
                  >
                    {renderSortLabel('currentStock', 'Current Stock')}
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Unit
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => handleRequestSort('supplier')}
                    sx={{ cursor: 'pointer' }}
                    align="center"
                  >
                    {renderSortLabel('supplier', 'Supplier')}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => handleRequestSort('reorderQuantity')}
                    sx={{ cursor: 'pointer' }}
                    align="center"
                  >
                    {renderSortLabel('reorderQuantity', 'Reorder Quantity')}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCandidates.map((item) => {
                  const key = `${item.inventoryId}-${item.productId}-${item.supplierId}`;
                  const unitId = selectedUnits[key] || item.defaultUnitId || '';
                  const brandVariety = [item.brand, item.variety].filter(Boolean).join(' - ');
                  const detailParts = [];
                  if (brandVariety) detailParts.push(brandVariety);
                  if (item.size) detailParts.push(item.size);
                  const suppliersForUnit = getSuppliersForUnit(item, unitId);
                  const selectedSupplierId =
                    selectedSuppliers[key] ||
                    getDefaultSupplierIdForUnit(item, unitId) ||
                    '';

                  const selectedUnit =
                    (item.unitTypes || []).find((u) => u.unitId === unitId) ||
                    (item.unitTypes && item.unitTypes[0]) ||
                    null;

                  const currentStockDisplay =
                    selectedUnit && typeof selectedUnit.currentStock === 'number'
                      ? selectedUnit.currentStock
                      : item.currentStock;

                  const currentStockDisplayValue =
                    typeof currentStockDisplay === 'number'
                      ? currentStockDisplay.toFixed(2)
                      : currentStockDisplay;

                  const targetStockDebug =
                    item.targetStock === null || item.targetStock === undefined
                      ? 'Not set'
                      : item.targetStock;

                  const debugCurrentStockBuying =
                    selectedUnit && typeof selectedUnit.currentStock === 'number'
                      ? selectedUnit.currentStock
                      : item.currentStock;

                  const debugCurrentStockSelling =
                    selectedUnit && typeof selectedUnit.currentStockSelling === 'number'
                      ? selectedUnit.currentStockSelling
                      : item.currentStockSelling;

                  const debugBuyToSellFactor =
                    selectedUnit && typeof selectedUnit.buyToSellFactor === 'number'
                      ? selectedUnit.buyToSellFactor
                      : item.buyToSellFactor;

                  const debugReorderQtySelling =
                    selectedUnit && typeof selectedUnit.reorderQuantitySelling === 'number'
                      ? selectedUnit.reorderQuantitySelling
                      : item.reorderQuantitySelling;

                  return (
                    <TableRow
                      key={key}
                      hover
                      data-product-name={item.productName}
                      data-user-id={item.userId}
                      data-product-id={item.productId}
                      data-inventory-id={item.inventoryId}
                      data-unit-id={unitId}
                      data-supplier-id={item.supplierId}
                      data-current-stock-selling={debugCurrentStockSelling}
                      data-buy-to-sell-factor={debugBuyToSellFactor}
                      data-reorder-qty-selling-initial={debugReorderQtySelling}
                      data-current-stock-buying={debugCurrentStockBuying}
                      data-low-stock-limit={item.stockLimit}
                      data-target-stock={targetStockDebug}
                      sx={
                        highlightedKey === key
                          ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
                          : undefined
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selectedMap[key]}
                          onChange={() => handleToggleRow(key)}
                        />
                      </TableCell>
                      <TableCell>
                        {item.productName}
                        {detailParts.length > 0 && ` - ${detailParts.join(', ')}`}
                      </TableCell>
                      <TableCell align="center">{currentStockDisplayValue}</TableCell>
                      <TableCell align="center">
                        <FormControl fullWidth>
                          <Select
                            value={unitId}
                            onChange={(e) => handleUnitChange(item, key, e.target.value)}
                          >
                            {(item.unitTypes || []).map((u) => (
                              <MenuItem key={u.unitId} value={u.unitId}>
                                {u.unitType}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        {suppliersForUnit.length === 0 ? (
                          <FormControl fullWidth>
                            <Select
                              value=""
                              disabled
                              displayEmpty
                              renderValue={() => <em>No suppliers linked</em>}
                            >
                              <MenuItem value="">
                                <em>No suppliers linked</em>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl fullWidth>
                            <Select
                              value={selectedSupplierId}
                              onChange={(e) =>
                                handleSupplierChange(key, e.target.value)
                              }
                            >
                              {suppliersForUnit.map((s) => (
                                <MenuItem
                                  key={s.supplierId}
                                  value={String(s.supplierId)}
                                >
                                  {formatSupplierLabel(s)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          value={reorderQuantities[key] ?? ''}
                          onChange={(e) =>
                            handleReorderQuantityChange(key, e.target.value)
                          }
                          inputProps={{ min: 0 }}
                          size="small"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <StyledTablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={sortedCandidates.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ display: 'flex', justifyContent: 'center' }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <Button variant="contained" color="primary" onClick={handleGenerateText}>
              Generate Reorder Text
            </Button>
          </Box>

          {messages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" gutterBottom align="center">
                  Generated Messages ({currentMessageIndex + 1} of {messages.length})
                </Typography>
                {messages.length > 1 && (
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      onClick={handlePreviousMessage}
                      disabled={currentMessageIndex === 0}
                      sx={{ mr: 1 }}
                    >
                      ◄ Previous
                    </Button>
                    <Button
                      onClick={handleNextMessage}
                      disabled={currentMessageIndex === messages.length - 1}
                    >
                      Next ►
                    </Button>
                  </Box>
                )}
              </Box>
              <TextField
                value={generatedText}
                onChange={() => {}}
                multiline
                minRows={6}
                fullWidth
              />
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Tooltip title="Send via WhatsApp">
                  <IconButton
                    color="success"
                    onClick={handleSendWhatsApp}
                    aria-label="Send via WhatsApp"
                  >
                    <WhatsAppIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    color="primary"
                    onClick={handleCopyToClipboard}
                    aria-label="Copy to clipboard"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const ViewInventories = () => {
  const [inventories, setInventories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editLimitOpen, setEditLimitOpen] = useState(false);
  const [editTargetOpen, setEditTargetOpen] = useState(false);
  const [editInventory, setEditInventory] = useState({});
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [inventoryToDelete, setInventoryToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [productNameFilter, setProductNameFilter] = useState('');
  const filterInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInventories = async () => {
      try {
        const response = await axiosInstance.get('/inventories');
        const inventoriesWithOriginalUnits = response.data.map((inventory) => ({
          ...inventory,
          originalUnitType: inventory.unit_type,
          originalStockLimit: inventory.stock_limit, // Store original stock_limit
          originalUnitId: inventory.unit_id, // Store original unit_id
          currentLimitUnitId: inventory.unit_id, // Track which unit the current limit is set in
        }));
        setInventories(inventoriesWithOriginalUnits);
      } catch (error) {
        setErrorMessage('Failed to fetch inventories. Please try again.');
        setOpenErrorSnackbar(true);
        console.error('Error fetching inventories:', error);
      }
    };

    if (isOnline) {
      fetchInventories();
    }

    const handleOnline = () => {
      setIsOnline(true);
      setErrorMessage('Network is back online. Updating data...');
      setOpenSuccessSnackbar(true);
      fetchInventories();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setErrorMessage('You are offline. Some actions may not be available.');
      setOpenErrorSnackbar(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  // Focus the filter input when filters are enabled
  useEffect(() => {
    if (showFilters && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [showFilters]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [showFilters, productNameFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUpdateLimitClick = (inventory) => {
    setEditInventory({
      ...inventory,
      stock_limit: inventory.stock_limit || 0
    });
    setEditLimitOpen(true);
  };

  const handleUpdateTargetClick = (inventory) => {
    setEditInventory({
      ...inventory,
      target_stock:
        inventory.target_stock === null || inventory.target_stock === undefined
          ? ''
          : inventory.target_stock,
    });
    setEditTargetOpen(true);
  };

  const handleTargetChange = (event) => {
    const { value } = event.target;
    setEditInventory({ ...editInventory, target_stock: value });
  };

  const handleTargetSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload =
        editInventory.target_stock === '' ||
        editInventory.target_stock === null ||
        editInventory.target_stock === undefined
          ? { target_stock: null }
          : { target_stock: parseFloat(editInventory.target_stock) };

      await axiosInstance.put(
        `/inventories/${editInventory.inventory_id}/target-stock`,
        payload
      );

      const updatedInventories = inventories.map((inv) =>
        inv.inventory_id === editInventory.inventory_id
          ? { ...inv, target_stock: payload.target_stock }
          : inv
      );

      setInventories(updatedInventories);
      setEditTargetOpen(false);
      setOpenSuccessSnackbar(true);
      setErrorMessage('Target stock updated successfully.');
    } catch (error) {
      console.error('Error updating target stock:', error);
      setErrorMessage('Error updating target stock.');
      setOpenErrorSnackbar(true);
    }
  };

  const confirmDelete = (inventory) => {
    setInventoryToDelete(inventory);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!isOnline) {
      setErrorMessage('Cannot delete inventory while offline.');
      setOpenErrorSnackbar(true);
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      await axiosInstance.delete(`/inventories/${inventoryToDelete.inventory_id}`);
      setInventories(inventories.filter((inv) => inv.inventory_id !== inventoryToDelete.inventory_id));
      setOpenSuccessSnackbar(true);
      setErrorMessage('Inventory deleted successfully.');
    } catch (error) {
      setErrorMessage('Failed to delete inventory. Please try again.');
      setOpenErrorSnackbar(true);
      console.error('Error deleting inventory:', error);
    } finally {
      setDeleteConfirmOpen(false);
      setInventoryToDelete(null);
    }
  };

  const handleReconcileClick = (inventoryId) => {
    navigate(`/dashboard/inventories/reconcile/${inventoryId}`);
  };

  const handleUnitConversion = async (inventoryId, toUnitId) => {
    if (!isOnline) {
      setErrorMessage('Cannot convert units while offline.');
      setOpenErrorSnackbar(true);
      return;
    }

    const inventory = inventories.find((inv) => inv.inventory_id === inventoryId);
    if (!inventory) return;

    try {
      // Convert current stock
      const stockResponse = await axiosInstance.post('/inventories/convert', {
        quantity: inventory.current_stock,
        fromUnitId: inventory.unit_id,
        toUnitId: toUnitId,
      });

      // Convert stock limit (reminder limit) from current unit to selected unit
      const limitResponse = await axiosInstance.post('/inventories/convert', {
        quantity: inventory.stock_limit, // Use current stock_limit for conversion
        fromUnitId: inventory.currentLimitUnitId, // Use current unit_id for conversion
        toUnitId: toUnitId,
      });

      const { convertedQuantity } = stockResponse.data;
      const { convertedQuantity: convertedStockLimit } = limitResponse.data;

      // Find the new unit type from available_units
      const newUnit = inventory.available_units.find(unit => unit.unit_id === toUnitId);
      const newUnitType = newUnit ? newUnit.unit_type : inventory.unit_type;

      setInventories(
        inventories.map((inv) =>
          inv.inventory_id === inventoryId
            ? { 
                ...inv, 
                current_stock: convertedQuantity, 
                stock_limit: convertedStockLimit,
                unit_id: toUnitId,
                unit_type: newUnitType, // Update the unit_type as well
                currentLimitUnitId: toUnitId // Update the currentLimitUnitId
              }
            : inv
        )
      );

      setOpenSuccessSnackbar(true);
      setErrorMessage('Conversion completed successfully.');
    } catch (error) {
      setErrorMessage('Error converting units. Please try again.');
      setOpenErrorSnackbar(true);
      console.error('Error converting units:', error);
    }
  };

  const handleLimitChange = (event) => {
    const { value } = event.target;
    setEditInventory({ ...editInventory, stock_limit: value });
  };

  const handleLimitSubmit = async (event) => {
    event.preventDefault();
    try {
      // Update the stock limit via API
      await axiosInstance.put(`/inventories/${editInventory.inventory_id}/limit`, {
        stock_limit: editInventory.stock_limit,
        unit_id: editInventory.unit_id // Send the unit_id to the backend
      });
      
      // Update the local state
      const updatedInventories = inventories.map(inv =>
        inv.inventory_id === editInventory.inventory_id 
          ? { ...inv, stock_limit: editInventory.stock_limit, currentLimitUnitId: editInventory.unit_id }
          : inv
      );
      setInventories(updatedInventories);
      setEditLimitOpen(false);
      setOpenSuccessSnackbar(true);
      setErrorMessage('Reminder limit updated successfully.');
    } catch (error) {
      console.error('Error updating reminder limit:', error);
      setErrorMessage('Error updating reminder limit.');
      setOpenErrorSnackbar(true);
    }
  };

  // Helper function to get unit display name with category
  const getUnitDisplayName = (inventory) => {
    if (!inventory.available_units || !inventory.unit_id) {
      return inventory.unit_type;
    }
    const currentUnit = inventory.available_units.find(unit => unit.unit_id === inventory.unit_id);
    if (currentUnit) {
      return `${currentUnit.unit_type} (${currentUnit.unit_category})`;
    }
    return inventory.unit_type;
  };

  const handleCloseSnackbar = () => {
    setOpenSuccessSnackbar(false);
    setOpenErrorSnackbar(false);
  };

  // Helper function to get filtered inventories based on name filter
  const getFilteredInventories = () => {
    if (!showFilters || !productNameFilter.trim()) {
      return inventories;
    }

    const trimmedFilter = productNameFilter.trim().toLowerCase();
    return inventories.filter(inventory => {
      const searchText = `${inventory.product_name || ''} ${inventory.brand || ''} ${inventory.variety || ''} ${inventory.size || ''}`.toLowerCase();
      return searchText.includes(trimmedFilter);
    });
  };

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom>
        Inventories
      </Typography>

      {/* Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showFilters}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setShowFilters(isChecked);
                if (!isChecked) {
                  setProductNameFilter('');
                }
              }}
              color="primary"
            />
          }
          label="Enable Filters"
        />
        {showFilters && (
          <TextField
            label="Filter by Product Info"
            variant="outlined"
            value={productNameFilter}
            onChange={(e) => setProductNameFilter(e.target.value)}
            placeholder="Search by product name, brand, variety, or size..."
            sx={{ maxWidth: 400, width: '100%' }}
            inputRef={filterInputRef}
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, marginBottom: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Product Name</StyledTableCell>
              <StyledTableCell>Current Stock</StyledTableCell>
              <StyledTableCell>Unit Type</StyledTableCell>
              <StyledTableCell>Reminder Limit</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredInventories()
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((inventory) => (
                <TableRow key={inventory.inventory_id}>
                  <TableCell>
                    {inventory.product_name}
                    {inventory.brand && ` (${inventory.brand})`}
                    {(inventory.variety || inventory.size) && ` - ${[inventory.variety, inventory.size].filter(Boolean).join(', ')}`}
                  </TableCell>
                  <TableCell>
                    {typeof inventory.current_stock === 'number'
                      ? inventory.current_stock.toFixed(2)
                      : parseFloat(inventory.current_stock).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth>
                      <Select
                        value={inventory.unit_id}
                        onChange={(e) => handleUnitConversion(inventory.inventory_id, e.target.value)}
                      >
                        {inventory.available_units && inventory.available_units.length > 0 ? (
                          inventory.available_units.map((unit) => (
                            <MenuItem key={unit.unit_id} value={unit.unit_id}>
                              {`${unit.unit_type} (${unit.unit_category})`}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>
                            No units available
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>{Math.ceil(inventory.stock_limit)}</TableCell>
                  <TableCell>
                    <Button 
                      color="secondary" 
                      onClick={() => confirmDelete(inventory)}
                      sx={{ mr: 1 }}
                    >
                      DELETE
                    </Button>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleReconcileClick(inventory.inventory_id)}
                      sx={{ 
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: 'primary.main',
                        mr: 1
                      }}
                    >
                      SET CURRENT STOCK
                    </Link>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleUpdateLimitClick(inventory)}
                      sx={{ 
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: 'primary.main'
                      }}
                    >
                      UPDATE LIMIT
                    </Link>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleUpdateTargetClick(inventory)}
                      sx={{ 
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: 'primary.main',
                        ml: 2,
                      }}
                    >
                      UPDATE TARGET STOCK
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <StyledTablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={getFilteredInventories().length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Dialog for updating reminder limit */}
      <Dialog open={editLimitOpen} onClose={() => setEditLimitOpen(false)}>
        <DialogTitle>Update Reminder Limit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Product: {editInventory.product_name}
              {editInventory.variety && ` - ${editInventory.variety}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current unit: {getUnitDisplayName(editInventory)}
            </Typography>
            <TextField
              label="Reminder Limit"
              type="number"
              value={editInventory.stock_limit || 0}
              onChange={handleLimitChange}
              fullWidth
              margin="normal"
              helperText={`Set the minimum stock level in ${getUnitDisplayName(editInventory)} to trigger low inventory alerts`}
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLimitOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLimitSubmit} color="primary" variant="contained">
            Update Limit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editTargetOpen} onClose={() => setEditTargetOpen(false)}>
        <DialogTitle>Update Target Stock</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Product: {editInventory.product_name}
              {editInventory.variety && ` - ${editInventory.variety}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current unit: {getUnitDisplayName(editInventory)}
            </Typography>
            <TextField
              label="Target Stock"
              type="number"
              value={
                editInventory.target_stock === null ||
                editInventory.target_stock === undefined
                  ? ''
                  : editInventory.target_stock
              }
              onChange={handleTargetChange}
              fullWidth
              margin="normal"
              helperText={`Set the desired stock level in ${getUnitDisplayName(editInventory)} after reordering`}
              inputProps={{ min: 0, step: "0.01" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTargetOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleTargetSubmit} color="primary" variant="contained">
            Update Target Stock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deletion */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this inventory item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSuccessSnackbar || openErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={openSuccessSnackbar ? 'success' : 'error'}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewInventories;
